import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { type AxiosPromise, isAxiosError } from 'axios'
import { type ClassValue, clsx } from 'clsx'
import i18next from 'i18next'
import toast from 'react-hot-toast'
import { extendTailwindMerge } from 'tailwind-merge'
import type { ZodError, ZodSchema, z } from 'zod'
import commonEn from '@/translations/en/common.json'

type CountryCode = keyof typeof commonEn.countries

const isCountryCode = (code: string): code is CountryCode =>
	Object.hasOwn(commonEn.countries, code)

/**
 * i18n key for a country's name from its ISO 3166-1 alpha-2 code, in either
 * case ("US", "fr"), or `undefined` for a code the list does not carry, so the
 * caller can fall back to the code itself rather than render a raw key.
 */
export const getCountryNameFromCode = (
	code: string | null | undefined
): string | undefined => {
	const lower = code?.toLowerCase()
	return lower && isCountryCode(lower)
		? i18next.t(`common:countries.${lower}`)
		: (code ?? undefined)
}

export const getInitials = (name: string) => {
	return name.match(/\b\w/g) || []
}

export const megabytesToBytes = (megabytes: number) => megabytes * 1_000_000

export const errorHandler = (
	error: unknown,
	keyField: 'message' | 'code' = 'message'
) => {
	if (isAxiosError(error)) {
		const key = error.response?.data?.[keyField]
		const message =
			key && error.status !== 500
				? i18next.t(`errors:${key}`)
				: i18next.t('errors:default')
		return toast.error(message)
	}

	toast.error(i18next.t('errors:default'))
	return error
}

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
	throw new Error(error)
}

export const objectKeys = Object.keys as <T extends object>(
	object: T
) => Array<keyof T>
export const objectValues = Object.values as <T extends object>(
	object: T
) => T[keyof T][]
export const objectEntries = Object.entries as <T extends object>(
	object: T
) => { [K in keyof T]: [K, T[K]] }[keyof T][]
export const objectFromEntries = Object.fromEntries as <
	K extends PropertyKey,
	V
>(
	entries: Iterable<readonly [K, V]>
) => { [P in K]: V }

export const isWindows = /windows/i.test(navigator.userAgent)

/* transform an array of items into a dictionary by utilizing a key selector function. */
export function groupBy<K extends PropertyKey, T>(
	items: Iterable<T>,
	keySelector: (item: T) => K
): Record<K, T[]> {
	const grouped = {} as Record<K, T[]>

	for (const item of items) {
		const key = keySelector(item)
		grouped[key] = grouped[key] || []
		grouped[key].push(item)
	}

	return grouped
}

export const formatCurrency = (amount: number, currency: string) =>
	new Intl.NumberFormat('en', {
		style: 'currency',
		currency
	}).format(amount)

export const formatLargeNumber = (num: number): string => {
	if (num >= 1_000_000) {
		return `$${(num / 1_000_000).toFixed(2)}M`
	}
	if (num >= 1000) {
		return `$${(num / 1000).toFixed(1)}K`
	}
	return `$${Math.round(num)}`
}

export const formatPercentage = (decimal: number): string => {
	return `${Math.round(decimal * 100)}%`
}

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
	callback: T,
	delay: number
): ((...args: Parameters<T>) => void) => {
	let timeout: ReturnType<typeof setTimeout>

	return (...args: Parameters<T>) => {
		clearTimeout(timeout)
		timeout = setTimeout(() => {
			callback(...args)
		}, delay)
	}
}

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 */
export const parseAxiosPromise = <T extends ZodSchema>({
	axiosPromise,
	responseShape,
	onZodError
}: {
	axiosPromise: AxiosPromise
	responseShape: T
	onZodError?: (error: ZodError) => void
}): Promise<z.infer<T>> =>
	axiosPromise.then((response) => {
		const safeParsedResponseData = responseShape.safeParse(response.data)
		if (!safeParsedResponseData.success) {
			console.error(safeParsedResponseData.error.message)
			onZodError?.(safeParsedResponseData.error)
			throw new Error(safeParsedResponseData.error.message)
		}

		return response.data
	})

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 */
export const parseResponseData = <TResponseData extends ZodSchema>({
	responseData,
	responseShape,
	onZodError
}: {
	responseData: unknown
	responseShape: TResponseData
	onZodError?: (error: ZodError) => void
}): z.infer<TResponseData> => {
	const safeParsedResponseData = responseShape.safeParse(responseData)
	if (!safeParsedResponseData.success) {
		console.error(safeParsedResponseData.error.message)
		onZodError?.(safeParsedResponseData.error)
		throw new Error(safeParsedResponseData.error.message)
	}
	return safeParsedResponseData.data
}

export const infiniteQueryFetchNextPage = async (
	infiniteQuery: UseInfiniteQueryResult
) => {
	if (infiniteQuery.hasNextPage && !infiniteQuery.isFetching) {
		await infiniteQuery.fetchNextPage()
	}
}

const customTwMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			'font-size': [
				'text-airbnb-display-lg',
				'text-airbnb-display-md',
				'text-airbnb-display-sm',
				'text-display-2xl',
				'text-display-xl',
				'text-display-lg',
				'text-display-md',
				'text-display-sm',
				'text-display-sm-fixed',
				'text-display-xs',
				'text-display-xs-fixed',
				'text-xl-fixed'
			]
			// https://github.com/dcastil/tailwind-merge/blob/v2.3.0/docs/recipes.md#adding-custom-scale-from-tailwind-config-to-tailwind-merge-config
			// https://github.com/dcastil/tailwind-merge/blob/v2.3.0/docs/configuration.md
		}
	}
})

export function cn(...inputs: ClassValue[]) {
	return customTwMerge(clsx(inputs))
}

export const onNumericInputChange = (
	e: React.ChangeEvent<HTMLInputElement>,
	onChange: (value: string) => void
) => {
	const value = e.target.value.replace(/[^0-9]/g, '')
	onChange(value)
}

export const encodeQueryParam = (value: string) => encodeURIComponent(value)
